import { ErrorHandler, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';

import { EMPTY, of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { SellerCreate, SellerService } from '../../http';

export interface SellerPersonalData {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  socialId: string | null;
}

export interface SellerBankData {
  bankCode: number | null;
  bankBranch: string | null;
  bankAccount: string | null;
  terms?: boolean;
}

export enum SellerCollectDataSteps {
  GoBack,
  PersonalData,
  BusinessData,
  Success,
}

const sellerCollectDataSteps = [
  SellerCollectDataSteps.GoBack,
  SellerCollectDataSteps.PersonalData,
  SellerCollectDataSteps.BusinessData,
  SellerCollectDataSteps.Success,
];

export interface SellerCollectDataState {
  currentStep: SellerCollectDataSteps;
  data: {
    personalData?: SellerPersonalData;
    bankData?: SellerBankData;
  };
}

@Injectable()
export class CreateSellerCollectDataStore extends ComponentStore<SellerCollectDataState> {
  public currentStep$ = this.select((state) => state.currentStep);
  public personalData$ = this.select((state) => state.data?.personalData);

  constructor(
    private readonly sellerService: SellerService,
    private readonly errors: ErrorHandler,
  ) {
    super();
  }

  public storePersonalData = this.effect<SellerPersonalData>((data$) => data$.pipe(tap((data) => this.setPersonalData(data))));

  public submitSellerData = this.effect<SellerBankData>((data$) =>
    data$.pipe(
      withLatestFrom(this.personalData$),
      switchMap(([bankData, personalData]) => {
        if (!personalData) {
          return of();
        }

        const sellerInfo = CreateSellerCollectDataStore.createDataForPersonalData(personalData);
        const bank = CreateSellerCollectDataStore.createDataForBusinessData(bankData);

        return this.sellerService.createSeller({ ...sellerInfo, ...bank }).pipe(
          map(() => bankData),
          catchError((err) => {
            this.errors.handleError(err);
            return EMPTY;
          }),
        );
      }),
      tap((data) => this.setBankData(data)),
    ),
  );

  private static createDataForPersonalData(data: SellerPersonalData): SellerCreate {
    return {
      seller_first_name: data.firstName || undefined,
      seller_last_name: data.lastName || undefined,
      seller_email: data.email || undefined,
      seller_phone: data.phone || undefined,
      seller_social_id: data.socialId || undefined,
    };
  }

  private static createDataForBusinessData(data: SellerBankData): SellerCreate {
    return {
      seller_bank_code: data.bankCode || undefined,
      seller_bank_branch: data.bankBranch || undefined,
      seller_bank_account_number: data.bankAccount || undefined,
    };
  }

  private setPersonalData = this.updater<SellerPersonalData>((state, data) => ({
    ...state,
    data: {
      ...state.data,
      personalData: data,
    },
    currentStep: SellerCollectDataSteps.BusinessData,
  }));

  private setBankData = this.updater<SellerBankData>((state, data) => ({
    ...state,
    data: {
      ...state.data,
      bankData: data,
    },
    currentStep: SellerCollectDataSteps.Success,
  }));

  public prevStep = this.updater((state) => {
    const prevIndex = sellerCollectDataSteps.indexOf(state.currentStep) - 1;
    return {
      ...state,
      currentStep: sellerCollectDataSteps[prevIndex >= 0 ? prevIndex : 0],
    };
  });
}
